import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import * as SelectPrimitive from "@radix-ui/react-select";
import { cva } from "class-variance-authority";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "../../utils";

type SelectSize = "default" | "xs";

interface SelectContextValue {
  label?: string;
  size: SelectSize;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext() {
  const context = React.useContext(SelectContext);
  if (context === null) {
    throw new Error("'useSelectValueContext' must be used within 'Select'");
  }
  return context;
}

type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> & {
  label?: string;
  /**
   * Controls the density of the whole select — trigger, dropdown items,
   * label and scroll buttons all follow it. `"xs"` renders the compact,
   * chip-style variant.
   */
  size?: SelectSize;
};

function Select({ label, size = "default", ...props }: SelectProps) {
  return (
    <SelectContext value={{ label, size }}>
      <SelectPrimitive.Root {...props} />
    </SelectContext>
  );
}

const SelectGroup = SelectPrimitive.Group;

const SelectValue = ({
  className,
  children,
  placeholder,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) => (
  <span
    className={cn("[&_[data-ds-select-item-description]]:af-hidden", className)}
  >
    <SelectPrimitive.Value
      placeholder={
        <>
          <SelectItemLabel />
          {placeholder}
        </>
      }
      {...props}
    >
      {children}
    </SelectPrimitive.Value>
  </span>
);
SelectValue.displayName = SelectPrimitive.Value.displayName;

const selectTriggerVariants = cva(
  "af-flex af-w-full af-items-center af-justify-between af-rounded-md af-border af-border-input af-bg-background af-ring-offset-background focus:af-outline-none focus:af-ring-2 focus:af-ring-ring focus:af-ring-offset-2 disabled:af-cursor-not-allowed disabled:af-text-muted-foreground disabled:af-opacity-50 data-[placeholder]:af-text-muted-foreground disabled:data-[placeholder]:af-text-muted-foreground [&>span]:af-truncate [&_svg]:af-shrink-0",
  {
    variants: {
      size: {
        default: "af-h-10 af-px-3 af-py-2 af-text-sm",
        xs: "af-gap-1 af-px-2 af-py-1 af-text-xs",
      },
      variant: {
        default: "",
        primary:
          "af-border-primary/50 af-bg-secondary af-bg-secondary/20 af-font-semibold af-text-primary data-[placeholder]:af-text-primary",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  },
);

const SelectTrigger = ({
  className,
  size,
  variant,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> &
  VariantProps<typeof selectTriggerVariants>) => {
  const { size: contextSize } = useSelectContext();
  const resolvedSize = size ?? contextSize;
  return (
    <SelectPrimitive.Trigger
      className={cn(
        selectTriggerVariants({ size: resolvedSize, variant }),
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown
          className={cn("af-size-4", {
            "af-opacity-50": resolvedSize !== "xs",
          })}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
};
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) => {
  const { size } = useSelectContext();
  return (
    <SelectPrimitive.ScrollUpButton
      className={cn(
        "af-flex af-cursor-default af-items-center af-justify-center af-py-1",
        className,
      )}
      {...props}
    >
      <ChevronUp
        className={cn("af-h-4 af-w-4", size === "xs" && "af-h-3 af-w-3")}
      />
    </SelectPrimitive.ScrollUpButton>
  );
};
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) => {
  const { size } = useSelectContext();
  return (
    <SelectPrimitive.ScrollDownButton
      className={cn(
        "af-flex af-cursor-default af-items-center af-justify-center af-py-1",
        className,
      )}
      {...props}
    >
      <ChevronDown
        className={cn("af-size-4", { "af-h-3 af-w-3": size === "xs" })}
      />
    </SelectPrimitive.ScrollDownButton>
  );
};
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = ({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) => {
  const { size } = useSelectContext();
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          "af-relative af-z-50 af-max-h-[--radix-select-content-available-height] af-min-w-[8rem] af-origin-[--radix-select-content-transform-origin] af-overflow-y-auto af-overflow-x-hidden af-rounded-md af-border af-bg-popover af-text-popover-foreground af-shadow-md data-[state=open]:af-animate-in data-[state=closed]:af-animate-out data-[state=closed]:af-fade-out-0 data-[state=open]:af-fade-in-0 data-[state=closed]:af-zoom-out-95 data-[state=open]:af-zoom-in-95 data-[side=bottom]:af-slide-in-from-top-2 data-[side=left]:af-slide-in-from-right-2 data-[side=right]:af-slide-in-from-left-2 data-[side=top]:af-slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:af-translate-y-1 data-[side=left]:af--translate-x-1 data-[side=right]:af-translate-x-1 data-[side=top]:af--translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn("af-p-1", {
            "af-flex af-flex-col af-gap-0.5": size === "xs",
            "af-h-[var(--radix-select-trigger-height)] af-w-full af-min-w-[var(--radix-select-trigger-width)]":
              position === "popper",
          })}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) => {
  const { size } = useSelectContext();
  return (
    <SelectPrimitive.Label
      className={cn(
        {
          "af-px-1 af-pb-2 af-pt-1 af-text-xs af-italic af-text-muted-foreground":
            size === "xs",
          "af-py-1.5 af-pl-8 af-pr-2 af-text-sm af-font-semibold":
            size !== "xs",
        },
        className,
      )}
      {...props}
    />
  );
};
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => {
  const { size } = useSelectContext();

  // XS renders each option as a compact, bordered "chip" with the selected
  // indicator on the right (per the design system's XS Select variant),
  // rather than the default left check-gutter row.
  if (size === "xs") {
    return (
      <SelectPrimitive.Item
        className={cn(
          "af-relative af-flex af-w-full af-cursor-default af-select-none af-items-center af-rounded-lg af-p-0.5 af-pr-6 af-outline-none focus:af-bg-accent data-[disabled]:af-pointer-events-none data-[disabled]:af-opacity-50 [&_[data-ds-select-item-label]]:af-hidden",
          className,
        )}
        {...props}
      >
        <span className="af-flex af-items-center af-overflow-hidden af-rounded-md af-border af-border-input af-bg-background af-px-2 af-py-1 af-text-xs af-font-medium af-text-foreground">
          <SelectPrimitive.ItemText className="af-flex af-flex-col af-gap-1.5">
            <SelectItemLabel />
            {children}
          </SelectPrimitive.ItemText>
        </span>

        <span className="af-absolute af-right-2 af-flex af-h-3 af-w-3 af-items-center af-justify-center">
          <SelectPrimitive.ItemIndicator>
            <Check className="af-h-3 af-w-3" />
          </SelectPrimitive.ItemIndicator>
        </span>
      </SelectPrimitive.Item>
    );
  }

  return (
    <SelectPrimitive.Item
      className={cn(
        "af-relative af-flex af-w-full af-cursor-default af-select-none af-items-center af-rounded-sm af-py-1.5 af-pl-8 af-pr-2 af-text-sm af-outline-none focus:af-bg-accent focus:af-text-popover-foreground data-[disabled]:af-pointer-events-none data-[disabled]:af-opacity-50 [&_[data-ds-select-item-label]]:af-hidden",
        className,
      )}
      {...props}
    >
      <span className="af-absolute af-left-2 af-flex af-h-3.5 af-w-3.5 af-items-center af-justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="af-h-4 af-w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText className="af-flex af-flex-col af-gap-1.5">
        {/* SelectItemLabel isn't visible in the dropdown menu because we add `af-hidden` className to it in the `SelectPrimitive.Item above` */}
        <SelectItemLabel />
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
};
SelectItem.displayName = SelectPrimitive.Item.displayName;

function SelectItemLabel() {
  const { label } = useSelectContext();
  if (!label) {
    return null;
  }

  return (
    <span
      data-ds-select-item-label
      className="af-mr-1.5 af-font-medium af-text-foreground"
    >
      {label}
    </span>
  );
}

const SelectSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator
    className={cn("af--mx-1 af-my-1 af-h-px af-bg-muted", className)}
    {...props}
  />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectItemDescription = ({
  className,
  ...props
}: React.ComponentProps<"p">) => (
  <p
    data-ds-select-item-description
    className={cn("af-text-xs af-text-muted-foreground", className)}
    {...props}
  />
);
SelectItemDescription.displayName = "SelectItemDescription";

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectItemDescription,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  selectTriggerVariants,
};
